/**
 * Money display helpers for the client portal. Amounts arrive as integer minor units
 * (cents, fils, yen...) and are never added across currencies.
 */

/** ISO 4217 currencies without minor units. */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'UYI', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/** ISO 4217 currencies with three decimal places. */
const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);

/** Number of decimal places of a currency (0, 2 or 3). */
export const currencyExponent = (currency: string): number => {
  const code = currency.trim().toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3;
  return 2;
};

/** The portal's language codes are not all BCP 47 tags (Albanian is stored as `al`). */
export const intlLocale = (language?: string | null): string => {
  if (!language) return 'en';
  if (language === 'al' || language === 'alb') return 'sq';
  return language;
};

/** Formats integer minor units as a currency amount, e.g. (123456, 'USD') → "$1,234.56". */
export const formatMinor = (amountMinor: number, currency: string, language?: string | null): string => {
  const code = currency.trim().toUpperCase();
  const exponent = currencyExponent(code);
  const major = amountMinor / 10 ** exponent;
  try {
    return new Intl.NumberFormat(intlLocale(language), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(major);
  } catch {
    return `${major.toFixed(exponent)} ${code}`;
  }
};

/** Formats a calendar date 'YYYY-MM-DD' without shifting it through UTC. Returns null for empty or bad input. */
export const formatCalendarDate = (value: string | null | undefined, language?: string | null): string | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(intlLocale(language), { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  } catch {
    return value.slice(0, 10);
  }
};

/** Sum of the amounts when every row has the given currency; null when the currencies differ. */
export const sumSameCurrency = (rows: Array<{ amount_minor: number | null; currency: string }>, currency: string): number | null => {
  let total = 0;
  for (const row of rows) {
    // A hidden amount (internal entry) or another currency: no honest total.
    if (row.amount_minor === null || row.currency.toUpperCase() !== currency.toUpperCase()) return null;
    total += row.amount_minor;
  }
  return total;
};
